import React, { useState, useMemo } from 'react';
import { PageLayout } from '@/shared/layout';
import {
  Button,
  Avatar,
  Badge,
  ActionToolbar,
  DataNavigator,
  StatusBadge
} from '@/components/ui';
import { useAuth } from '@/core/auth/AuthContext';
import { useCompanyMembers } from './hooks/useCompanyMembers';
import { usePermission } from '@/core/permissions/usePermission';
import { InviteMemberModal } from './components/InviteMemberModal';
import { RoleAssignDrawer } from './components/RoleAssignDrawer';
import type { CompanyMember } from './types/company.types';
import { getRoleLabel } from '@/utils/role';
import {
  UserPlus,
  Settings2,
  Trash2,
  Mail,
  Calendar,
  Shield,
  UserCog,
  Eye,
  MoreVertical
} from 'lucide-react';

export default function CompanyMembersPage() {
  const { can } = usePermission();
  const { profile } = useAuth();
  const companyId = profile?.company_id;
  const {
    members,
    isLoading,
    inviteMember,
    updateRole,
    removeMember,
    isProcessing
  } = useCompanyMembers(companyId || '');

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<CompanyMember | null>(null);
  const [isRoleDrawerOpen, setIsRoleDrawerOpen] = useState(false);

  const columns = useMemo(() => [
    {
      key: 'member_info',
      title: 'Thành viên',
      render: (_: any, item: CompanyMember) => (
        <div className="flex items-center gap-3">
          <Avatar
            src={item.avatar_path}
            name={item.full_name}
            size="md"
            className="rounded-xl border border-white shadow-sm"
          />
          <div className="flex flex-col">
            <span className="font-black text-slate-900 leading-tight">{item.full_name}</span>
            <span className="text-[10px] text-slate-400 font-bold tracking-tight">{item.email}</span>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      title: 'Vai trò',
      render: (_: any, item: CompanyMember) => (
        <Badge className="bg-blue-50 text-blue-600 border-blue-100 font-black text-[10px]  tracking-wider">
          {getRoleLabel(item.role_code)}
        </Badge>
      )
    },
    {
      key: 'status',
      title: 'Trạng thái',
      render: (_: any, item: CompanyMember) => (
        <StatusBadge status={item.is_active ? 'active' : 'inactive'} type="default" className="font-black" />
      )
    },
    {
      key: 'joined',
      title: 'Gia nhập',
      render: (_: any, item: CompanyMember) => (
        <div className="flex flex-col">
          <span className="text-xs font-bold text-slate-700">
            {new Date(item.joined_at).toLocaleDateString('vi-VN')}
          </span>
          <span className="text-[10px] text-slate-400 font-black ">Thành viên chính thức</span>
        </div>
      )
    },
    {
      key: 'actions',
      title: '',
      align: 'right' as const,
      render: (_: any, item: CompanyMember) => (
        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedMember(item);
              setIsRoleDrawerOpen(true);
            }}
          >
            <UserCog className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:bg-red-50 hover:text-red-600"
            onClick={() => {
              if (confirm(`Bạn có chắc muốn xóa ${item.full_name} khỏi tổ chức?`)) {
                removeMember(item.id);
              }
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ], [removeMember]);

  const mobileCardRender = (item: CompanyMember) => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar
            src={item.avatar_path}
            name={item.full_name}
            size="lg"
            className="rounded-2xl border border-white shadow-sm"
          />
          <div className="flex flex-col">
            <h4 className="font-black text-slate-900 leading-tight">{item.full_name}</h4>
            <Badge variant="outline" className="p-0 text-[10px] font-black  tracking-widest text-blue-600">
               {getRoleLabel(item.role_code)}
            </Badge>
          </div>
        </div>
        <StatusBadge status={item.is_active ? 'active' : 'inactive'} type="default" size="sm" />
      </div>

      <div className="grid grid-cols-1 gap-2 bg-white/50 p-3 rounded-2xl border border-white shadow-sm">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Mail className="w-3.5 h-3.5 text-slate-400" />
          <span className="truncate font-medium">{item.email}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Calendar className="w-3.5 h-3.5 text-slate-400" />
          <span className="truncate font-medium">Gia nhập: {new Date(item.joined_at).toLocaleDateString('vi-VN')}</span>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
         <Button
            variant="ghost"
            size="sm"
            className="h-9 px-4 rounded-xl font-black text-[10px]  tracking-widest"
            onClick={() => {
              setSelectedMember(item);
              setIsRoleDrawerOpen(true);
            }}
          >
            Phân quyền
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 w-9 p-0 rounded-xl text-red-500 hover:bg-red-50"
            onClick={() => {
              if (confirm(`Bạn có chắc muốn xóa ${item.full_name} khỏi tổ chức?`)) {
                removeMember(item.id);
              }
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Thành viên Hệ thống"
      description="Quản lý nhân sự, phân quyền và cộng tác trong tổ chức"
      breadcrumbs={[{ label: 'Cấu hình', path: '/settings' }, { label: 'Thành viên' }]}
    >
      <div className="space-y-6">
        <ActionToolbar
          searchPlaceholder="Tìm theo tên, email..."
          primaryActionLabel="Mời thành viên"
          primaryActionIcon={<UserPlus className="w-4 h-4" />}
          onPrimaryAction={() => setIsInviteModalOpen(true)}
          isLoading={isLoading || isProcessing}
          primaryActionDisabled={!can('companies', 'invite_member')}
          searchValue=""
          onSearchChange={() => {}}
          actions={
            <div className="mr-2 text-[10px] font-black text-slate-400  tracking-widest hidden md:block">
              Tổng số: <span className="text-slate-900 text-sm">{members.length}</span> nhân sự
            </div>
          }
        />

        <DataNavigator
          columns={columns}
          data={members}
          isLoading={isLoading}
          mobileCardRender={mobileCardRender}
        />

        <InviteMemberModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          onInvite={async (email, role) => {
            await inviteMember({ email, roleCode: role });
          }}
        />

        <RoleAssignDrawer
          isOpen={isRoleDrawerOpen}
          onClose={() => setIsRoleDrawerOpen(false)}
          member={selectedMember}
          onAssign={async (role) => {
            if (selectedMember) {
              await updateRole({ memberId: selectedMember.id, roleCode: role });
              setIsRoleDrawerOpen(false);
            }
          }}
        />
      </div>
    </PageLayout>
  );
}
